import { useCallback, useMemo } from 'react';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';

/**
 * Authorization hooks.
 *
 * Post-Mongo-pivot the source of truth for roles and permissions is the
 * backend's /permissions/me endpoint, surfaced through PermissionsProvider.
 * `useAuth().has()` from Clerk is NOT used here — Clerk only owns identity
 * (login + org membership); the role/permission set has moved into Mongo.
 *
 * The public surface (usePermissions / useHasPermission / useCrudPermissions
 * / usePageAccess / useIsAdmin / useHasFullAccess) is unchanged so existing
 * call sites keep working without edits.
 *
 * Permission strings accept both prefixed (`org:emissions:create`) and bare
 * (`emissions:create`) forms — the provider's `has()` normalises before
 * looking up.
 */

/**
 * Custom hook to check permissions.
 *
 * @param {Object|Array|String} permissions - Can be:
 *   - Object with aliases: { canCreate: 'org:emissions:create', canRead: 'org:emissions:read' }
 *   - Array of permissions: ['org:emissions:create', 'org:emissions:read']
 *   - Single permission string: 'org:emissions:create'
 *
 * @returns {Object} Permission check results
 *
 * @example
 * const { canCreate, canRead } = usePermissions({
 *   canCreate: 'org:emissions:create',
 *   canRead: 'org:emissions:read'
 * });
 */
export const usePermissions = (permissions) => {
  const { has } = usePermissionsContext();

  return useMemo(() => {
    if (!permissions) return {};

    if (typeof permissions === 'string') {
      return { hasPermission: has({ permission: permissions }) };
    }

    if (Array.isArray(permissions)) {
      return permissions.reduce((acc, p) => {
        acc[p] = has({ permission: p });
        return acc;
      }, {});
    }

    if (typeof permissions === 'object') {
      return Object.entries(permissions).reduce((acc, [key, p]) => {
        acc[key] = has({ permission: p });
        return acc;
      }, {});
    }

    return {};
  }, [has, permissions]);
};

/**
 * Hook to check a single permission.
 *
 * @param {String} permission
 * @returns {Boolean}
 */
export const useHasPermission = (permission) => {
  const { has } = usePermissionsContext();
  return useMemo(() => {
    if (!permission) return false;
    return has({ permission });
  }, [has, permission]);
};

/**
 * Hook for the standard CRUD shape. The `edit` action matches the Lincoln
 * Electric User Matrix vocabulary (Read / Create / Edit / Delete).
 * `canManage` is exposed as an alias to `canEdit` for legacy callers.
 *
 * @param {String} resource - e.g. 'emissions', 'diagrams', 'sell_orders'
 * @returns {{ canCreate, canRead, canEdit, canManage, canDelete }}
 */
export const useCrudPermissions = (resource) => {
  const result = usePermissions({
    canCreate: `org:${resource}:create`,
    canRead: `org:${resource}:read`,
    canEdit: `org:${resource}:edit`,
    canDelete: `org:${resource}:delete`,
  });
  return { ...result, canManage: result.canEdit };
};

// Maps an ACTIONS value → the matching CRUD boolean field returned by the
// hook. Used by `gate()`, `gateProps()`, and `allow()` to translate a
// requested action into a permission lookup.
const ACTION_TO_FLAG = {
  read: 'canRead',
  create: 'canCreate',
  edit: 'canEdit',
  delete: 'canDelete',
};

const isValidAction = (action) =>
  typeof action === 'string' && Object.prototype.hasOwnProperty.call(ACTION_TO_FLAG, action);

const warnInvalidAction = (where, action) => {
  if (process.env.NODE_ENV === 'production') return;
  // eslint-disable-next-line no-console
  console.warn(
    `[usePageAccess.${where}] Invalid action "${action}". Use one of: ${Object.keys(
      ACTION_TO_FLAG,
    ).join(', ')}. Import ACTIONS from permissionsMatrix.js for safety.`,
  );
};

/**
 * Page-access hook for the User Matrix. Returns CRUD booleans plus three
 * pure helpers that cover the common gating shapes — array descriptors,
 * spread-prop objects, and single values.
 *
 *   - `gate(items)` filters an array of `{ requires?, ...rest }` descriptors,
 *     stripping `requires` from the returned items.
 *   - `gateProps(action, propsMap)` returns the same object shape with every
 *     value set to `undefined` when the action is denied.
 *   - `allow(action, value)` returns `value` when granted, `undefined` otherwise.
 *   - `canRead / canCreate / canEdit / canDelete` — direct booleans.
 *
 * @param {String} resourceKey - One of the keys from permissionsMatrix.js
 */
export const usePageAccess = (resourceKey) => {
  const perms = useCrudPermissions(resourceKey);
  const { canRead, canCreate, canEdit, canDelete } = perms;

  const allow = useCallback(
    (action, value) => {
      if (!isValidAction(action)) {
        warnInvalidAction('allow', action);
        return undefined;
      }
      return perms[ACTION_TO_FLAG[action]] ? value : undefined;
    },
    [perms],
  );

  const gate = useCallback(
    (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .filter((item) => {
          if (!item) return false;
          if (!item.requires) return true;
          if (!isValidAction(item.requires)) {
            warnInvalidAction('gate', item.requires);
            return false;
          }
          return Boolean(perms[ACTION_TO_FLAG[item.requires]]);
        })
        .map((item) => {
          if (!item.requires) return item;
          // eslint-disable-next-line no-unused-vars
          const { requires, ...rest } = item;
          return rest;
        });
    },
    [perms],
  );

  const gateProps = useCallback(
    (action, props) => {
      if (!props || typeof props !== 'object') return {};
      if (!isValidAction(action)) {
        warnInvalidAction('gateProps', action);
        return Object.fromEntries(Object.keys(props).map((k) => [k, undefined]));
      }
      const allowed = Boolean(perms[ACTION_TO_FLAG[action]]);
      const result = {};
      for (const key of Object.keys(props)) {
        result[key] = allowed ? props[key] : undefined;
      }
      return result;
    },
    [perms],
  );

  return { canRead, canCreate, canEdit, canDelete, gate, gateProps, allow };
};

/**
 * Strict admin check for the Roles & Permissions admin UI. Reads
 * `me.isAdmin` from /permissions/me directly — the backend computes this
 * once and includes it in the payload, so we no longer have to enumerate
 * legacy role keys here.
 */
export const useIsAdmin = () => {
  const { me } = usePermissionsContext();
  return Boolean(me?.isAdmin);
};

/**
 * Broader "is this user effectively unrestricted in the matrix" check.
 * Post-pivot this collapses to `isAdmin` — `basic_member` no longer exists
 * as a special-case (the Mongo schema doesn't carry it), and any user the
 * backend considers omnipotent is flagged via `isAdmin`. Kept as a separate
 * export so call sites that semantically want "treat as full access" don't
 * have to switch to `useIsAdmin` and don't accidentally tighten gating if
 * the two checks ever diverge again.
 */
export const useHasFullAccess = () => useIsAdmin();
